// Formatting helpers for Tensor Hive marketplace data.
// All token amounts stored in the database are now in LAMPORTS (native SOL, 9 decimals).
// 1 SOL = 1_000_000_000 lamports. Typical agent prices range 0.0001-0.0005 SOL.

/** Convert lamports to a human SOL float, e.g. 100000 -> 0.0001 */
export function lamportsToSol(lamports: number | undefined | null): number {
  return (lamports ?? 0) / 1_000_000_000;
}

/** Format a lamports integer as a human SOL string, e.g. 100000 -> "0.0001 SOL" */
export function formatSol(lamports: number | undefined | null, decimals = 4): string {
  const value = lamportsToSol(lamports);
  if (value === 0) return '0 SOL';
  return `${value.toFixed(decimals)} SOL`;
}

/** Truncate a Solana address for display, e.g. "Abc123...xyz9" */
export function truncateAddress(address: string | undefined | null, chars = 4): string {
  if (!address) return '';
  if (address.length <= chars * 2 + 3) return address;
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/** Split a comma-separated capabilityTags string into a clean tag array */
export function parseCapabilityTags(tags: string | undefined | null): string[] {
  if (!tags) return [];
  return tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** statsRating is stars * 10 (e.g. 49 -> 4.9). Returns formatted "4.9" */
export function formatRating(statsRating: number | undefined | null): string {
  return ((statsRating ?? 0) / 10).toFixed(1);
}

/** Returns a time-of-day greeting */
export function timeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return 'Good Night';
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
}

/** Format a large number with commas */
export function formatCount(value: number | undefined | null): string {
  return (value ?? 0).toLocaleString('en-US');
}

/** Format a duration in seconds as a compact human string, e.g. 3661 -> "1h 1m" */
export function formatDuration(totalSeconds: number | undefined | null): string {
  const seconds = Math.max(0, Math.floor(totalSeconds ?? 0));
  if (seconds <= 0) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!days && !hours) parts.push(`${secs}s`);
  return parts.slice(0, 2).join(' ') || '0s';
}

/** Turn a display name into a URL-safe slug, e.g. "My Cool Agent!" -> "my-cool-agent" */
export function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'agent';
}
